import React, { useState, useEffect } from 'react';
import StudentNavbar from '../../components/StudentNavbar';
import '../../styles/StudentWall.css';
import './MyClasses.css';
import { useNavigate } from 'react-router-dom';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Alert, 
  CircularProgress,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import config from '../../config.json';
import axios from '../../utils/axiosInstance';
import url from '../../url.json';

const menuGroups = [
  {
    title: '我的學習',
    items: [
      { icon: InventoryIcon, text: '我的課程', active: false, onClick: () => window.location.href='/student/my-courses'},
      { icon: SchoolIcon, text: '我的社群', active: true, onClick: () => window.location.href='/student/my-classes'},
    ],
  },
];

const MyClasses = () => {
  const navigate = useNavigate();
  const [joinFormOpen, setJoinFormOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [selectedYear, setSelectedYear] = useState('全部');
  const [currentRole, setCurrentRole] = useState('student');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [openStudentsDialog, setOpenStudentsDialog] = useState(false);
  const [selectedClassStudents, setSelectedClassStudents] = useState([]);

  // 後端資料
  const [classes, setClasses] = useState([]);

  // 載入學生社群清單（使用後端真實資料）
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        setError('');
        console.log('[MyClasses] 開始載入學生社群清單');
        console.log('[MyClasses] API:', config.getUserClasses);
        const resp = await axios.get(config.getUserClasses);
        console.log('[MyClasses] 原始回應:', resp.data);
        // 允許後端返回不同結構，做寬鬆映射
        const raw = resp.data?.data || resp.data || [];
        console.log('[MyClasses] 正規化前資料筆數:', Array.isArray(raw) ? raw.length : 0);
        const mapped = (Array.isArray(raw) ? raw : []).map((c) => ({
          // 基本鍵
          id: c.classId || c.Class?.id || c.id || c.class_id,
          name: c.Class?.name || c.name || c.className || '未命名社群',
          // 後端巢狀欄位攤平
          schoolYear: c.Class?.year || c.year || '-',
          semester: c.Class?.semester || c.semester || '-',
          code: c.Class?.inviteCode || c.inviteCode || c.code || '-',
          teacher: c.Class?.User?.name || c.teacher?.name || c.teacherName || '老師',
          // 學生資訊
          studentCount: (typeof c.classStudentCount === 'number') ? c.classStudentCount : (Array.isArray(c.students) ? c.students.length : 0),
          students: Array.isArray(c.students) ? c.students.map(s => s.name || s.Student?.name || '-') : [],
          // 保留狀態
          status: c.status || 'active'
        }));
        console.log('[MyClasses] 正規化後資料筆數:', mapped.length);
        setClasses(mapped);
      } catch (err) {
        console.error('載入社群失敗:', err);
        console.log('[MyClasses] 錯誤回應內容:', err?.response?.data);
        setError(err?.response?.data?.message || '載入社群失敗');
        setClasses([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
  }, []);

  // 篩選班級資料
  const filteredClasses = classes.filter(classItem => {
    const matchesSearch = !searchTerm || 
      classItem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (classItem.teacher && classItem.teacher.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesYear = selectedYear === '全部' || classItem.schoolYear === selectedYear;
    
    return matchesSearch && matchesYear;
  });

  // 獲取學年度選項
  const getYearOptions = () => {
    const years = [...new Set(classes.map(cls => cls.schoolYear))];
    return ['全部', ...years.sort()];
  };

  // 加入班級（透過邀請碼加入社群）
  const handleJoinClass = async () => {
    if (!joinCode.trim()) {
      setJoinError('請輸入邀請碼');
      return;
    }

    // 驗證邀請碼格式（應該是6位數字）
    const trimmedCode = joinCode.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setJoinError('邀請碼格式錯誤，請輸入6位數字');
      return;
    }

    try {
      setJoinLoading(true);
      setJoinError('');
      
      console.log('[MyClasses] 開始加入社群，邀請碼:', trimmedCode);
      const response = await axios.post(
        `${url.backendHost}${config.joinClass}`,
        { inviteCode: trimmedCode }
      );

      console.log('[MyClasses] 加入社群成功:', response.data);

      if (response.data.success) {
        // 成功加入社群
        alert(response.data.message || '成功加入社群！');
        
        // 清空輸入欄位和錯誤訊息
        setJoinCode('');
        setJoinError('');
        
        // 關閉對話框
        setJoinFormOpen(false);
        
        // 重新載入社群清單
        const fetchClasses = async () => {
          try {
            setLoading(true);
            setError('');
            const resp = await axios.get(config.getUserClasses);
            const raw = resp.data?.data || resp.data || [];
            const mapped = (Array.isArray(raw) ? raw : []).map((c) => ({
              id: c.classId || c.Class?.id || c.id || c.class_id,
              name: c.Class?.name || c.name || c.className || '未命名社群',
              schoolYear: c.Class?.year || c.year || '-',
              semester: c.Class?.semester || c.semester || '-',
              code: c.Class?.inviteCode || c.inviteCode || c.code || '-',
              teacher: c.Class?.User?.name || c.teacher?.name || c.teacherName || '老師',
              studentCount: (typeof c.classStudentCount === 'number') ? c.classStudentCount : (Array.isArray(c.students) ? c.students.length : 0),
              students: Array.isArray(c.students) ? c.students.map(s => s.name || s.Student?.name || '-') : [],
              status: c.status || 'active'
            }));
            setClasses(mapped);
          } catch (err) {
            console.error('重新載入社群失敗:', err);
            setError(err?.response?.data?.message || '載入社群失敗');
          } finally {
            setLoading(false);
          }
        };
        fetchClasses();
      } else {
        throw new Error(response.data.message || '加入社群失敗');
      }
    } catch (error) {
      console.error('加入班級失敗:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          '加入班級失敗，請檢查邀請碼是否正確';
      setJoinError(errorMessage);
    } finally {
      setJoinLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return '進行中';
      case 'completed':
        return '已結束';
      default:
        return '未知';
    }
  };

  const handleViewStudents = async (classItem) => {
    try {
      setOpenStudentsDialog(true);
      // 即時從後端抓取該社群學生名單（如果有 API）
      // 例如：const resp = await axios.get(`${config.getClassStudentsNew}${classItem.id}`);
      // 映射為名稱陣列後 setSelectedClassStudents(names)
      if (Array.isArray(classItem.students) && classItem.students.length > 0) {
        setSelectedClassStudents(classItem.students);
      } else {
        setSelectedClassStudents([]);
      }
    } catch (e) {
      console.error('載入學生名單失敗:', e);
      setSelectedClassStudents([]);
    }
  };

  return (
    <div className="teacher-layout my-classes-container">
      <div className="main-content-v2">
        <StudentNavbar menuGroups={menuGroups} />
        
        <div className="class-header-row">
          <h1 className="main-title">我的社群</h1>
        </div>
        
        {/* 篩選區域在標題下方靠右 */}
        <div className="filter-container">
          <TextField
            placeholder="🔍 搜尋社群"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            className="filter-search-input"
          />
          <FormControl size="small" className="filter-select">
            <InputLabel>學年度</InputLabel>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              label="學年度"
            >
              {getYearOptions().map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <button
            className="btn-secondary filter-clear-btn"
            onClick={() => {
              setSearchTerm('');
              setSelectedYear('全部');
            }}
          >
            清除篩選
          </button>
        </div>

        {/* 社群列表 */}
        <div className="activity-card-list grid-responsive">
          {/* 加入社群卡片 */}
          <div
            className="activity-card add-activity"
            style={{ 
              position: 'relative', 
              background:'#2b687f', 
              color: 'white',
              boxShadow:'0 2px 8px rgba(44,44,80,0.08)', 
              borderRadius: '12px', 
              border:'1.5px solid #2b687f',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              minHeight: '200px',
              maxHeight: '200px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.transition = 'all 0.3s ease';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.transition = 'all 0.3s ease';
            }}
            onClick={() => setJoinFormOpen(true)}
          >
            <div style={{ fontSize: '3rem', marginBottom: '16px', color: 'white' }}>
              +
            </div>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', marginBottom: '8px' }}>
              加入新社群
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              點擊輸入邀請碼加入社群
            </Typography>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
              <Typography variant="h6" sx={{ ml: 2 }}>載入中...</Typography>
            </div>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : filteredClasses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                目前沒有社群
              </Typography>
              <Typography variant="body2" color="text.secondary">
                請點擊「加入社群」卡片來加入新的社群
              </Typography>
            </div>
          ) : (
            filteredClasses.map((classItem) => (
              <div
                className="activity-card"
                key={classItem.id}
                style={{ 
                  position: 'relative', 
                  background:'#fff', 
                  boxShadow:'0 2px 8px rgba(44,44,80,0.08)', 
                  borderRadius: '12px', 
                  border:'1.5px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  minHeight: '200px',
                  maxHeight: '200px',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(44,44,80,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(44,44,80,0.08)';
                }}
                onClick={() => handleViewStudents(classItem)}
              >
                <div className="activity-name" style={{ 
                  fontSize: '1.2rem', 
                  marginBottom: '8px',
                  padding: '0 8px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.4',
                  minHeight: '2.8rem'
                }}>
                  {classItem.name}
                </div>
                
                {/* 社群標籤：學年度、學期 */}
                <div style={{ 
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  padding: '0 8px',
                  marginBottom: '8px'
                }}>
                  <Chip 
                    label={classItem.schoolYear} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                  <Chip 
                    label={classItem.semester}
                    size="small" 
                    color="secondary" 
                    variant="outlined"
                  />
                </div>

                {/* 社群資訊 */}
                <div style={{
                  padding: '0 8px',
                  fontSize: '0.85rem',
                  color: '#666',
                  lineHeight: '1.3'
                }}>
                  <div style={{ marginBottom: '4px' }}>
                    老師：{classItem.teacher}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    學生：{classItem.studentCount}人
                  </div>
                  <div>
                    邀請碼：{classItem.code}
                  </div>
                </div>

                {/* 狀態標籤 */}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px'
                }}>
                  <Chip 
                    label={getStatusText(classItem.status)} 
                    size="small" 
                    color={getStatusColor(classItem.status)}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        {/* 加入社群對話框 */}
        <Dialog 
          open={joinFormOpen} 
          onClose={() => {
            setJoinFormOpen(false);
            setJoinCode('');
            setJoinError('');
          }} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>加入社群</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="邀請碼"
                value={joinCode}
                onChange={(e) => {
                  setJoinCode(e.target.value);
                  setJoinError(''); // 清除錯誤訊息
                }}
                fullWidth
                placeholder="請輸入6位數字邀請碼"
                error={!!joinError}
                helperText={joinError || '邀請碼為6位數字'}
                inputProps={{
                  maxLength: 6,
                  pattern: '[0-9]*',
                  inputMode: 'numeric'
                }}
                onKeyPress={(e) => {
                  // 只允許輸入數字
                  if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                    e.preventDefault();
                  }
                  // 按 Enter 鍵提交
                  if (e.key === 'Enter' && !joinLoading && joinCode.trim().length === 6) {
                    handleJoinClass();
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setJoinFormOpen(false);
                setJoinCode('');
                setJoinError('');
              }}
              disabled={joinLoading}
            >
              取消
            </Button>
            <Button 
              onClick={handleJoinClass} 
              variant="contained" 
              className="btn-primary"
              disabled={joinLoading}
            >
              {joinLoading ? <CircularProgress size={20} /> : '加入社群'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 學生名單對話框 */}
        <Dialog open={openStudentsDialog} onClose={() => setOpenStudentsDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>社群成員名單</DialogTitle>
          <DialogContent>
            <List>
              {selectedClassStudents.map((student, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemText 
                    primary={student}
                    secondary={`成員 ${index + 1}`}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenStudentsDialog(false)} className="btn-primary">關閉</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default MyClasses;